import { useContext } from "react";
import styled, {
  css,
  DefaultTheme,
  FlattenInterpolation,
  ThemeProps,
} from "styled-components";
import {
  Alert,
  Alerts,
  AlertsContext,
  Flex,
  FormErrorTypes,
  SideBar,
  useCheckIfSubscriptionFeatureActiveBroker,
  ViewerContext,
} from "dash-ui";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import BreadcrumbNavigation from "flow-components/BreadcrumbNavigation/BreadcrumbNavigation";
import InactiveSubscriptionBanner from "common/components/InactiveSubscriptionBanner/InactiveSubscriptionBanner";

import { IContainerProps } from "./Container.interfaces";
import { sidebarItemsList } from "./Container.constants";

const ContainerWrapper = styled(Flex)`
  align-self: center;
  justify-content: center;
  width: 100%;
  flex-grow: 0;
`;

const ContainerStyle = styled(Flex)<{ size: "xl" | "lg" }>`
  background-color: ${({ theme }) => theme.colors.b10};
  min-height: calc(100vh - 110px);
  min-width: 0;
  box-sizing: border-box;
  max-width: ${({ theme, size }) => theme.breakpoints[size]}px;

  @media ${({ theme }) => theme.devices.mobile} {
    padding-left: ${({ theme }) => theme.space * 4}px;
    padding-right: ${({ theme }) => theme.space * 4}px;
    min-height: calc(100vh - 210px);
  }
  @media ${({ theme }) => theme.devices.tablet} {
    padding-left: ${({ theme }) => theme.space * 6}px;
    padding-right: ${({ theme }) => theme.space * 6}px;
  }
`;
const BreadcrumbWrapper = styled<{ stickyBreadcrumbs: boolean } & any>(Flex)`
  background-color: inherit;

  ${({ stickyBreadcrumbs }) =>
    stickyBreadcrumbs
      ? css`
          top: 50px;
          position: sticky;
          z-index: 4;
        `
      : ""}
`;
const Content = styled(Flex)<{
  css?: FlattenInterpolation<ThemeProps<DefaultTheme>> | string;
}>`
  background-color: ${({ theme }) => theme.colors.b10};
  width: 100%;
  box-sizing: border-box;
  ${({ css }) => css}
`;

function Container({
  children,
  contentStyle,
  withBreadcrumbs = true,
  breadcrumbsProps,
  withSidebar = true,
  showContent,
  size = "xl",
  className = "",
}: IContainerProps) {
  const { t } = useTranslation("common");
  const { state } = useContext(AlertsContext);
  const { broker } = useContext(ViewerContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { isSubscriptionActive } = useCheckIfSubscriptionFeatureActiveBroker();

  return (
    <ContainerWrapper flexDirection="row" w="inherit" className={className}>
      {withSidebar && (
        <SideBar
          variation="secondary"
          items={sidebarItemsList.map(({ label, icon, path, disabled }) => ({
            label: t(`labels.${label}`),
            icon,
            isActive: location.pathname.includes(path),
            disabled,
            onClick: () => {
              navigate(path);
              document.title = t(`labels.browserTabTitles.${label}`);
            },
          }))}
        />
      )}

      <ContainerStyle
        flex={1}
        px={11}
        pt={6}
        pb={4}
        flexDirection="column"
        w="100%"
        size={size}
      >
        {!isSubscriptionActive && !showContent && (
          <InactiveSubscriptionBanner />
        )}
        {(isSubscriptionActive || showContent) && (
          <>
            {withBreadcrumbs && (
              <BreadcrumbWrapper
                alignItems="center"
                justifyContent="space-between"
                w="100%"
                pb={4}
                pt={5}
                stickyBreadcrumbs={breadcrumbsProps?.sticky ?? false}
              >
                <BreadcrumbNavigation />
                {state[Alerts.ChangesSaved] && (
                  <Alert
                    type={FormErrorTypes.Valid}
                    alertType={Alerts.ChangesSaved}
                  >
                    {t("alerts.changesSaved")}
                  </Alert>
                )}
              </BreadcrumbWrapper>
            )}
            <Content flex={1} css={contentStyle}>
              {children}
            </Content>
          </>
        )}
      </ContainerStyle>
    </ContainerWrapper>
  );
}

export default Container;
